'use client';
import { useEffect, useState } from 'react';
import { Article as ArticleType } from '@/types/types';
import slugify from 'slugify';
import Link from 'next/link';

export default function TableOfContent({ post }: { post: ArticleType | null }) {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Initialize activeId on page load based on the current hash in the URL
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setActiveId(hash.substring(1)); // Remove the '#' from the hash
    }
  }, []);

  // Track the active section while scrolling using IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let currentId = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentId = entry.target.id;
          }
        });
        if (currentId) setActiveId(currentId);
      },
      {
        rootMargin: '0px 0px -50% 0px', // Trigger the observer when the element is halfway through the viewport
      }
    );

    let sections = document.querySelectorAll('.tableOfContent');
    if (sections.length === 0) {
      sections = document.querySelectorAll('.fallbackTableOfContent');
    }

    sections.forEach((section) => {
      observer.observe(section);
    });

    // Cleanup observer when component unmounts
    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, []);

  const handleClick = (id: string) => {
    setActiveId(id); // Set the active item when clicked
  };

  let tableOfContent = post?.content
    .filter((block: any) => block._type === 'tableOfContent')
    .map((block: any) => ({
      label: block.label,
      id: block.id,
    }));

  // fall back to the default table of content ie every h tag has an id
  if (!tableOfContent?.length) {
    tableOfContent = [
      {
        label: 'Introduction',
        id: 'introduction',
      },
    ];

    if (post?.content) {
      tableOfContent.push(
        ...post.content
          .filter((block: any) => block._type === 'block')
          .filter(
            (block: any) =>
              block.style === 'h2' ||
              block.style === 'h3' ||
              block.style === 'h1'
          )
          .map((block: any) => ({
            label: block.children[0].text,
            id: slugify(block.children[0].text, { lower: true }),
          }))
      );
    }
  }

  return (
    <div className='hidden w-1/5 flex-col gap-4 md:flex'>
      {!!tableOfContent?.length && (
        <div className='sticky top-20 flex flex-col gap-3'>
          <h2 className='font-sans text-xl font-semibold text-charcoal'>
            Table of Content
          </h2>
          <ul className='flex flex-col gap-3 pl-0.5'>
            {tableOfContent &&
              tableOfContent.map((item) => (
                <li
                  key={item.id}
                  className={`font-sans text-sm text-[#737373] ${
                    activeId === item.id ? 'font-semibold' : 'font-normal'
                  }`}
                >
                  <Link
                    href={`#${item.id}`}
                    onClick={() => handleClick(item.id)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
